import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { updateTournament } from 'Src/helpers/service/adminSU/tournamentsTable';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TournamentPublicSiteSettingsForm } from '../TournamentPublicSiteSettingsForm/TournamentPublicSiteSettingsForm';
import { TournamentEditTournamentTVForm } from '../TournamentEditTournamentTVForm/TournamentEditTournamentTVForm';
import { PUBLIC_SITE_STATUS_OPTIONS } from 'Src/consts/tournament';
import {
  getSUPublicSiteTournamentPage,
  getTournamentPublicSiteLink,
  getTournamentTVPublicSiteLink
} from 'Src/helpers/link/link';
import { UnsavedChangesModal } from 'Src/components/UnsavedChangesModal/UnsavedChangesModal';

type PublishingSection = 'public' | 'bigscreen';

interface Props {
  user: AppUser;
  tournament: Tournament;
  onUpdated: (tournament: Tournament) => void;
}

interface State {
  section: PublishingSection;
  isPublicEditorOpen: boolean;
  isTvEditorOpen: boolean;
  isSaving: boolean;
  saveError: string;
  isPublicDirty: boolean;
  isTvDirty: boolean;
  isDiscardOpen: boolean;
  pendingClose: 'public' | 'tv' | null;
}

const YesNo = ({ value }: { value: boolean }) => (
  <span className={`utmPublishing__valueChip ${value ? 'is-yes' : 'is-no'}`}>{value ? 'Yes' : 'No'}</span>
);

export class TournamentPublishing extends React.Component<Props, State> {
  state: State = {
    section: 'public',
    isPublicEditorOpen: false,
    isTvEditorOpen: false,
    isSaving: false,
    saveError: '',
    isPublicDirty: false,
    isTvDirty: false,
    isDiscardOpen: false,
    pendingClose: null
  };

  requestClose = (editor: 'public' | 'tv') => {
    const isDirty = editor === 'public' ? this.state.isPublicDirty : this.state.isTvDirty;
    if (isDirty) return this.setState({ isDiscardOpen: true, pendingClose: editor });
    this.closeEditor(editor);
  };

  closeEditor = (editor: 'public' | 'tv') => {
    this.setState({
      isPublicEditorOpen: editor === 'public' ? false : this.state.isPublicEditorOpen,
      isTvEditorOpen: editor === 'tv' ? false : this.state.isTvEditorOpen,
      isPublicDirty: editor === 'public' ? false : this.state.isPublicDirty,
      isTvDirty: editor === 'tv' ? false : this.state.isTvDirty,
      isDiscardOpen: false,
      pendingClose: null,
      saveError: ''
    });
  };

  save = async (data: any, close: 'public' | 'tv') => {
    if (this.state.isSaving) return;
    this.setState({ isSaving: true, saveError: '' });
    try {
      const tournament = await updateTournament(this.props.user, data, this.props.tournament.id);
      this.props.onUpdated(tournament);
      this.setState({
        isSaving: false,
        saveError: '',
        isPublicDirty: close === 'public' ? false : this.state.isPublicDirty,
        isTvDirty: close === 'tv' ? false : this.state.isTvDirty,
        isPublicEditorOpen: close === 'public' ? false : this.state.isPublicEditorOpen,
        isTvEditorOpen: close === 'tv' ? false : this.state.isTvEditorOpen
      });
    } catch (error) {
      const status = (error as any)?.response?.status;
      const serverMessage = (error as any)?.response?.data?.error || (error as any)?.response?.data?.message;
      this.setState({
        isSaving: false,
        saveError:
          status === 409
            ? 'This domain is already in use. Choose a different domain for this tournament.'
            : serverMessage || 'The publishing settings could not be saved. Please try again.'
      });
    }
  };

  toggleStage = async (stageType: string, enabled: boolean) => {
    const next = { ...(this.props.tournament.publicBigscreenStagesEnabled || {}), [stageType]: enabled };
    await this.save({ publicBigscreenStagesEnabled: next }, 'tv');
  };

  renderPublic = () => {
    const tournament = this.props.tournament;
    const access =
      PUBLIC_SITE_STATUS_OPTIONS[tournament.publicSite?.status] || tournament.publicSite?.status || 'Disabled';
    const publicSiteUrl = tournament.domain ? getTournamentPublicSiteLink(tournament) : '';
    const bookingPageUrl = getSUPublicSiteTournamentPage(tournament, this.props.user);
    return (
      <>
        <article className="utmPublishing__card">
          <header>
            <div>
              <h2>Public site</h2>
              <p>
                What the outside world sees. Everything on this tab affects the public tournament page and the union
                website.
              </p>
            </div>
            <button
              className="is-primary"
              type="button"
              onClick={() => this.setState({ isPublicEditorOpen: true, saveError: '' })}
            >
              Edit public site
            </button>
          </header>
          <div className="utmPublishing__booleanGrid">
            <div>
              <span>Publish tournament on union website</span>
              <YesNo value={tournament.isPublishOnTheWebsite} />
            </div>
            <div>
              <span>Highlight tournament</span>
              <YesNo value={tournament.isPromoteTournament} />
            </div>
            <div>
              <span>Allowed for following in mobile app</span>
              <YesNo value={tournament.allowedForFollowing} />
            </div>
          </div>
          <div className="utmPublishing__facts">
            <div className="is-wide">
              <span>Details / booking page</span>
              <strong>
                <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer">
                  {bookingPageUrl}
                </a>
              </strong>
            </div>
            <div>
              <span>Website access</span>
              <strong>{access}</strong>
            </div>
            <div>
              <span>Domain name</span>
              <strong>{tournament.domain || '—'}</strong>
            </div>
            <div className="is-wide">
              <span>Public site link</span>
              <strong>
                {publicSiteUrl && tournament.publicSite?.status !== 'DISABLED' ? (
                  <a href={publicSiteUrl} target="_blank" rel="noopener noreferrer">
                    {publicSiteUrl}
                  </a>
                ) : (
                  'Not available while the public site is disabled'
                )}
              </strong>
            </div>
            <div>
              <span>External link</span>
              <strong>{tournament.link || '—'}</strong>
            </div>
            <div>
              <span>Disclaimer</span>
              <strong>{tournament.disclaimer || '—'}</strong>
            </div>
          </div>
        </article>
        <article className="utmPublishing__card utmPublishing__appearances">
          <header>
            <div>
              <h2>Where this tournament appears</h2>
            </div>
          </header>
          <div>
            <p>
              <span>Union website listing</span>
              <b>{tournament.isPublishOnTheWebsite ? 'Visible' : 'Hidden'}</b>
            </p>
            <p>
              <span>Public tournament page</span>
              <b>{access}</b>
            </p>
            <p>
              <span>Mobile app following</span>
              <b>{tournament.allowedForFollowing ? 'On' : 'Off'}</b>
            </p>
            <p>
              <span>Bigscreen / TV</span>
              <b>{PUBLIC_SITE_STATUS_OPTIONS[tournament.publicBigscreenSite?.status] || 'Disabled'}</b>
            </p>
          </div>
        </article>
      </>
    );
  };

  renderBigscreen = () => {
    const tournament = this.props.tournament;
    const enabled = tournament.publicBigscreenStagesEnabled || ({} as any);
    const tvUrl = tournament.domain ? getTournamentTVPublicSiteLink(tournament) : '';
    return (
      <>
        <article className="utmPublishing__card">
          <header>
            <div>
              <h2>Bigscreen / Tournament TV</h2>
              <p>The venue screen loop. Pick which stages appear and how long each page is shown.</p>
            </div>
            <button type="button" onClick={() => this.setState({ isTvEditorOpen: true })}>
              Manage TV
            </button>
          </header>
          <div className="utmPublishing__stageList">
            {(tournament.tournamentLayout || []).map(stage => (
              <label key={stage.id}>
                <span>
                  <strong>{stage.name}</strong>
                  <small>Show {stage.name.toLowerCase()} standings and fixtures in the loop.</small>
                </span>
                <input
                  type="checkbox"
                  checked={Boolean((enabled as any)[stage.type])}
                  disabled={this.state.isSaving}
                  onChange={event => this.toggleStage(stage.type, event.target.checked)}
                />
              </label>
            ))}
            {!tournament.tournamentLayout?.length ? (
              <p className="utmPublishing__empty">No competition stages have been configured.</p>
            ) : null}
          </div>
        </article>
        <article className="utmPublishing__card">
          <header>
            <div>
              <h2>Access</h2>
            </div>
            <button type="button" onClick={() => this.setState({ isPublicEditorOpen: true })}>
              Edit access
            </button>
          </header>
          <div className="utmPublishing__facts">
            <div>
              <span>Bigscreen access</span>
              <strong>{PUBLIC_SITE_STATUS_OPTIONS[tournament.publicBigscreenSite?.status] || 'Disabled'}</strong>
            </div>
            <div>
              <span>Password</span>
              <strong>{tournament.publicBigscreenSite?.password || '—'}</strong>
            </div>
            <div className="is-wide">
              <span>Bigscreen link</span>
              <strong>
                {tvUrl && tournament.publicBigscreenSite?.status !== 'DISABLED' ? (
                  <a href={tvUrl} target="_blank" rel="noopener noreferrer">
                    {tvUrl}
                  </a>
                ) : (
                  'Not available while Bigscreen is disabled'
                )}
              </strong>
            </div>
          </div>
        </article>
      </>
    );
  };

  render() {
    return (
      <main className="utmPublishing">
        <nav className="utmPublishing__subtabs">
          <button
            type="button"
            className={this.state.section === 'public' ? 'is-active' : ''}
            onClick={() => this.setState({ section: 'public' })}
          >
            Public site
          </button>
          <button
            type="button"
            className={this.state.section === 'bigscreen' ? 'is-active' : ''}
            onClick={() => this.setState({ section: 'bigscreen' })}
          >
            Bigscreen / TV
          </button>
        </nav>
        <div className="utmPublishing__content">
          {this.state.section === 'public' ? this.renderPublic() : this.renderBigscreen()}
        </div>
        <SimpleModal
          isOpen={this.state.isPublicEditorOpen}
          title="Publishing"
          customClass="utmPublishingEditor"
          dialogStyle={{ maxWidth: 820 }}
          showFooter={false}
          onCloseClick={() => this.requestClose('public')}
          onBackdropClick={() => this.requestClose('public')}
        >
          <TournamentPublicSiteSettingsForm
            user={this.props.user}
            tournament={this.props.tournament}
            serverError={this.state.saveError}
            onDomainChange={() => this.state.saveError && this.setState({ saveError: '' })}
            onDirtyChange={isPublicDirty => this.setState({ isPublicDirty })}
            onCancel={() => this.requestClose('public')}
            onSubmit={data => this.save(data, 'public')}
          />
        </SimpleModal>
        <SimpleModal
          isOpen={this.state.isTvEditorOpen}
          title="Manage Tournament TV"
          customClass="utmPublishingTvEditor"
          dialogStyle={{ maxWidth: 1120 }}
          showFooter={false}
          onCloseClick={() => this.requestClose('tv')}
          onBackdropClick={() => this.requestClose('tv')}
        >
          <TournamentEditTournamentTVForm
            tournament={this.props.tournament}
            onDirtyChange={isTvDirty => this.setState({ isTvDirty })}
            onCancel={() => this.requestClose('tv')}
            onSubmit={data => this.save(data, 'tv')}
          />
        </SimpleModal>
        <UnsavedChangesModal
          isOpen={this.state.isDiscardOpen}
          message="You have unsaved publishing changes. If you close now, those changes will be lost."
          onKeepEditing={() => this.setState({ isDiscardOpen: false, pendingClose: null })}
          onDiscard={() => this.state.pendingClose && this.closeEditor(this.state.pendingClose)}
        />
      </main>
    );
  }
}
